import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import {
  assetHazardLabel,
  getAssetHazardCells,
  getRiskDelta,
  formatIndicatorValue,
  HORIZON_LABELS,
  type AssetHazardResult,
} from "@/lib/results";
import { getResultsForHorizon } from "@/lib/results-filters";
import { PendingScore } from "./PendingScore";
import { DroughtScores } from "./DroughtScores";
import { HazardStatusCells, isSingleMainSeason, RiskBadge, SeasonTag } from "./StatusCell";
import { assetTypeName, IMPORTANCE_ORDER } from "./asset-display";
import { MapPin } from "lucide-react";
import type { Asset, AssetType, ValueChainStage } from "@/lib/assets";
import { HAZARD_TYPES, type ClimateScenario, type HazardType, type TimeHorizon } from "@shared/scoring/types";

interface ByAssetViewProps {
  assets: Asset[];
  assetTypes: AssetType[];
  stages: ValueChainStage[];
  visibleHazards: HazardType[]; // from the shared Hazards multi-select filter (already resolved to "all 6" when the filter is empty — see AssessmentDetailedResults.tsx)
  results: AssetHazardResult[]; // full, unsliced — every horizon x scenario combination
  scenario: ClimateScenario;
  activeHorizon: TimeHorizon;
  activeHorizons: TimeHorizon[]; // ordered, from the shared multi-select filter
  onHorizonChange: (horizon: TimeHorizon) => void;
}

function sortAssetsByImportanceThenName(assets: Asset[]): Asset[] {
  return [...assets].sort((a, b) => {
    const importanceDiff = IMPORTANCE_ORDER[a.businessImportance] - IMPORTANCE_ORDER[b.businessImportance];
    if (importanceDiff !== 0) return importanceDiff;
    return a.name.localeCompare(b.name);
  });
}

export function ByAssetView({
  assets,
  assetTypes,
  stages,
  visibleHazards,
  results,
  scenario,
  activeHorizon,
  activeHorizons,
  onHorizonChange,
}: ByAssetViewProps) {
  const sortedAssets = sortAssetsByImportanceThenName(assets);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(sortedAssets[0]?.id ?? null);
  const orderedVisibleHazards = HAZARD_TYPES.filter((h) => visibleHazards.includes(h));
  const selectedAsset = sortedAssets.find((a) => a.id === selectedAssetId) ?? sortedAssets[0] ?? null;

  const resultsForActiveHorizon = getResultsForHorizon(results, activeHorizon, scenario);
  const resultsForHistorical = getResultsForHorizon(results, "historical", scenario);

  return (
    <div className="flex flex-col gap-3">
      {activeHorizons.length > 1 && (
        <div className="flex flex-col gap-1.5 self-end">
          <span className="text-xs font-medium text-muted-foreground">Showing horizon</span>
          <Select value={activeHorizon} onValueChange={(v) => onHorizonChange(v as TimeHorizon)}>
            <SelectTrigger className="h-7 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeHorizons.map((h) => (
                <SelectItem key={h} value={h}>
                  {HORIZON_LABELS[h]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_2fr]">
        <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border bg-background/60 px-4 py-3">
            <span className="flex items-center gap-2 text-xs font-bold uppercase leading-4 tracking-[0.08em]">
              <span className="size-2 rounded-full bg-primary" />
              {sortedAssets.length} asset{sortedAssets.length === 1 ? "" : "s"}
            </span>
            <span className="text-xs text-muted-foreground">Ordered by importance</span>
          </div>
          {sortedAssets.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No assets match the current filters.</p>
          )}
          {sortedAssets.map((asset) => {
            const isSelected = asset.id === selectedAsset?.id;
            return (
              <button
                key={asset.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => setSelectedAssetId(asset.id)}
                className={cn(
                  "flex flex-col gap-2 border-l-4 border-t border-t-border px-4 py-3 text-left transition-colors first:border-t-0",
                  isSelected ? "border-l-primary bg-primary/10" : "border-l-transparent hover:bg-muted",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{asset.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {assetTypeName(asset, assetTypes)} · {asset.city}, {asset.country}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span className="text-[10px] uppercase leading-4 text-muted-foreground">Importance:</span>
                    <Badge className={LEVEL_BADGE_CLASSES[asset.businessImportance]}>
                      {LEVEL_LABELS[asset.businessImportance]}
                    </Badge>
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {orderedVisibleHazards.map((hazardType) => {
                    const cells = getAssetHazardCells(asset.id, hazardType, resultsForActiveHorizon);
                    return (
                      <div key={hazardType} className="flex flex-col items-start gap-0.5">
                        <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
                          {assetHazardLabel(hazardType, cells)}
                        </span>
                        <HazardStatusCells cells={cells} seasonInLabel renderComputed={(cell) => <RiskBadge cell={cell} />} />
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>

        {selectedAsset ? (
          <AssetDetailPanel
            asset={selectedAsset}
            assetTypes={assetTypes}
            stages={stages}
            visibleHazards={orderedVisibleHazards}
            resultsForActiveHorizon={resultsForActiveHorizon}
            resultsForHistorical={resultsForHistorical}
          />
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card p-8 text-sm text-muted-foreground">
            Select an asset to see its detail.
          </div>
        )}
      </div>
    </div>
  );
}

/** Per-season values on one line ("MAM … OND …") for Changing Precipitation;
 * a single value for every other hazard and for a single main-rainy-season
 * site (its hazard label names the season). */
function SeasonalValues({
  cells,
  render,
}: {
  cells: AssetHazardResult[];
  render: (cell: AssetHazardResult | undefined, index: number) => ReactNode;
}) {
  if (!cells.some((c) => c.season) || isSingleMainSeason(cells)) return <>{render(cells[0], 0)}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {cells.map((c, i) => (
        <span key={c.season} className="inline-flex items-center gap-1">
          <SeasonTag season={c.season} />
          {render(c, i)}
        </span>
      ))}
    </span>
  );
}

function DeltaIndicator({ delta }: { delta: number | undefined }) {
  if (delta === undefined) return <span className="text-sm text-muted-foreground">—</span>;
  if (delta === 0) return <span className="text-sm text-muted-foreground">stable</span>;
  const isUp = delta > 0;
  return (
    <span className={cn("text-sm font-medium", isUp ? "text-red-600" : "text-emerald-700")}>
      {isUp ? "↑" : "↓"} {Math.abs(delta)}
    </span>
  );
}

function AssetDetailPanel({
  asset,
  assetTypes,
  stages,
  visibleHazards,
  resultsForActiveHorizon,
  resultsForHistorical,
}: {
  asset: Asset;
  assetTypes: AssetType[];
  stages: ValueChainStage[];
  visibleHazards: HazardType[];
  resultsForActiveHorizon: AssetHazardResult[];
  resultsForHistorical: AssetHazardResult[];
}) {
  const rows = visibleHazards.map((hazardType) => {
    const cells = getAssetHazardCells(asset.id, hazardType, resultsForActiveHorizon);
    return {
      hazardType,
      cells,
      deltas: (cells.length ? cells : [undefined]).map((c) =>
        getRiskDelta(asset.id, hazardType, resultsForActiveHorizon, resultsForHistorical, c?.season),
      ),
    };
  });

  const scenarioCaveats = rows
    .flatMap((r) => r.cells.map((c) => c.scenarioCaveat))
    .filter((c): c is string => Boolean(c));
  const uniqueCaveats = Array.from(new Set(scenarioCaveats));
  const stageName = stages.find((stage) => stage.id === asset.valueChainStageId)?.name ?? null;

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-col gap-1 border-b border-border bg-background/60 p-6">
        <div className="flex flex-wrap items-center gap-2.5 text-xs">
          <span className="rounded-sm bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase leading-4 tracking-[0.08em] text-primary">
            Selected facility
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="font-semibold">{assetTypeName(asset, assetTypes)}</span>
          {stageName && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">
                Stage: <span className="font-bold text-foreground">{stageName}</span>
              </span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold leading-7">{asset.name}</h2>
          <Badge className={LEVEL_BADGE_CLASSES[asset.businessImportance]}>{LEVEL_LABELS[asset.businessImportance]}</Badge>
        </div>
        <p className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            {asset.city}, {asset.country}
          </span>
          {asset.latitude && asset.longitude && (
            <span className="font-mono">
              GPS {Number(asset.latitude).toFixed(4)}, {Number(asset.longitude).toFixed(4)}
            </span>
          )}
        </p>
      </div>

      <div className="overflow-x-auto p-6">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs font-bold uppercase tracking-[0.08em] text-slate-400">
              <th className="py-2 pr-3">Hazard</th>
              <th className="py-2 pr-3">Exposure</th>
              <th className="py-2 pr-3">Vulnerability</th>
              <th className="py-2 pr-3">Risk</th>
              <th className="py-2 pr-3">Level</th>
              <th className="py-2 pr-3">Indicator</th>
              <th className="py-2">vs today</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ hazardType, cells, deltas }) => (
              <tr key={hazardType} className="border-b border-border/60 last:border-0">
                <td className="py-2 pr-3 font-medium">{assetHazardLabel(hazardType, cells)}</td>
                <td className="py-2 pr-3">
                  <HazardStatusCells
                    cells={cells}
                    seasonInLabel
                    renderComputed={(c) =>
                      c.exposure === undefined ? (
                        <PendingScore />
                      ) : (
                        <Badge className={LEVEL_BADGE_CLASSES[c.exposure]}>{c.exposure}</Badge>
                      )
                    }
                  />
                </td>
                <td className="py-2 pr-3">
                  <HazardStatusCells
                    cells={cells}
                    seasonInLabel
                    renderComputed={(c) =>
                      c.vulnerability === undefined ? (
                        <PendingScore />
                      ) : (
                        <Badge className={LEVEL_BADGE_CLASSES[c.vulnerability]}>{c.vulnerability}</Badge>
                      )
                    }
                  />
                </td>
                <td className="py-2 pr-3">
                  <HazardStatusCells
                    cells={cells}
                    seasonInLabel
                    renderComputed={(c) =>
                      c.risk === undefined ? <PendingScore /> : <Badge className={LEVEL_BADGE_CLASSES[c.risk]}>{c.risk}</Badge>
                    }
                  />
                </td>
                <td className="py-2 pr-3">
                  <HazardStatusCells cells={cells} seasonInLabel renderComputed={(c) => <RiskBadge cell={c} />} />
                </td>
                <td className="py-2 pr-3 text-muted-foreground">
                  <SeasonalValues
                    cells={cells}
                    render={(c) =>
                      hazardType === "drought" && c?.droughtDetail ? (
                        <DroughtScores detail={c.droughtDetail} isFuture={c.horizon !== "historical"} />
                      ) : (
                        formatIndicatorValue(hazardType, c?.indicatorValue) ?? "—"
                      )
                    }
                  />
                </td>
                <td className="py-2">
                  <SeasonalValues cells={cells} render={(_, i) => <DeltaIndicator delta={deltas[i]} />} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-border bg-background/60 px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase leading-4 tracking-[0.08em] text-muted-foreground">
            Risk scale:
          </span>
          {([1, 2, 3, 4] as const).map((level) => (
            <span
              key={level}
              className={cn(
                "rounded-sm border px-2 py-0.5 text-xs font-medium",
                LEVEL_BADGE_CLASSES[level],
              )}
            >
              {level} {LEVEL_LABELS[level]}
            </span>
          ))}
          <span className="rounded-sm border border-border px-2 py-0.5 text-xs text-muted-foreground">
            No data
          </span>
        </div>
        {/* Risk is scored 1-4, not 0-4: "no data" and "not applicable" are
            statuses, not a zero level (client/src/lib/report-basis.ts,
            BASIS_TEXT.scale). Importance IS a scoring input -- it feeds
            Vulnerability via combineVulnerability() in
            shared/scoring/vulnerability.ts, per functional spec §2.2. */}
        <p className="text-xs leading-5 text-muted-foreground">
          Scores are whole numbers from 1 to 4. Gross risk is exposure crossed with vulnerability through the
          scoring matrix — not a multiplication. Asset importance is one of the two inputs to vulnerability,
          alongside the physical impact typical for the asset type.
        </p>
        {uniqueCaveats.map((caveat) => (
          <p key={caveat} className="text-xs leading-5 text-muted-foreground">{caveat}</p>
        ))}
      </div>
    </div>
  );
}
